<app-modal [isOpen]="isOpen" (close)="onClose()" className="max-w-3xl mx-4 sm:mx-6">
  @if (business) {
    <!--
      `overflow-hidden` + `shrink-0` en cabecera/pestañas/pie y `min-h-0` en el cuerpo.
      Sin esto, el cuerpo (que en la pestaña «Línea de tiempo» crece con cada evento y con
      cada diff expandido) empuja el flex-shrink por defecto y APLASTA la barra de pestañas
      —que puede encogerse hasta 0 porque tiene `overflow-x-auto`—, recortando los títulos
      «Información» / «Línea de tiempo». Mismo patrón que workflow-detail-modal.
    -->
    <div class="flex flex-col max-h-[85vh] overflow-hidden">
      <!-- Header -->
      <div class="shrink-0 flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-start gap-3 min-w-0">
          @if (business.logoUrl) {
            <img [src]="business.logoUrl" alt="Logo"
              class="h-12 w-12 shrink-0 rounded-lg object-contain border border-gray-200 dark:border-gray-700 bg-white p-1" />
          } @else {
            <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-lg font-semibold text-white bg-brand-500">
              {{ (business.name || '?').charAt(0).toUpperCase() }}
            </span>
          }
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white truncate">{{ business.name }}</h3>
              <app-badge [color]="getEstadoColor()" size="sm">{{ getEstadoLabel() }}</app-badge>
            </div>
            @if (business.description) {
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ business.description }}</p>
            }
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="shrink-0 flex border-b border-gray-200 dark:border-gray-700 px-6 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        @for (tab of tabs; track tab.id) {
          <button type="button" (click)="setTab(tab.id)"
            [class.border-brand-500]="activeTab === tab.id"
            [class.text-brand-600]="activeTab === tab.id"
            [class.border-transparent]="activeTab !== tab.id"
            [class.text-gray-500]="activeTab !== tab.id"
            class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap">
            {{ tab.label }}
          </button>
        }
      </div>

      <!-- Content -->
      <div class="flex-1 min-h-0 overflow-y-auto px-6 py-5">

        <!-- ===== INFORMACIÓN ===== -->
        @if (activeTab === 'info') {
          <div class="space-y-5">
            <section>
              <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Identificación</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Tipo de identificación</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ business.identificationType || '-' }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Número de identificación</p>
                  <p class="text-gray-800 dark:text-gray-200 break-all">{{ business.identificationNumber || '-' }}</p>
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Contacto</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Teléfono</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ business.phone || '-' }}</p>
                </div>
                <div class="sm:col-span-2">
                  <p class="text-xs text-gray-500">Dirección</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ business.address || '-' }}</p>
                </div>
              </div>
            </section>

            <section>
              <div class="flex items-center justify-between mb-2">
                <h4 class="text-xs font-semibold text-gray-500 uppercase">Administradores vinculados</h4>
                @if (detailLoading) {
                  <span class="inline-block h-3 w-3 rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-transparent animate-spin"></span>
                }
              </div>

              @if (!detailLoading && ((business.admins ?? []).length === 0)) {
                <p class="text-sm text-gray-500 dark:text-gray-400">Sin administrador asignado.</p>
              } @else if (!detailLoading) {
                <div class="space-y-2">
                  @for (a of (business.admins ?? []); track a.id) {
                    <div class="p-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/40">
                      <div class="flex items-start justify-between gap-2">
                        <div class="min-w-0">
                          <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {{ a.fullname }} {{ a.lastname || '' }}
                          </p>
                          <p class="text-xs text-gray-500 dark:text-gray-400 break-all">{{ a.email }}</p>
                        </div>
                        @if (a.roleName) {
                          <app-badge [color]="a.roleId === 1 ? 'primary' : 'info'" size="sm">{{ a.roleName }}</app-badge>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </section>

            <section>
              <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Metadata</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Zona horaria</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ business.defaultTimezone || 'Navegador' }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Moneda por defecto</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ business.defaultCurrencyCode || business.defaultCurrencyName || '-' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Fecha de creación</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ formatDate(business.datetimeCreate) }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Última actualización</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ formatDate(business.updatedAt) }}</p>
                </div>
              </div>
            </section>
          </div>
        }

        <!-- ===== LÍNEA DE TIEMPO ===== -->
        @if (activeTab === 'timeline') {
          <app-audit-timeline
            [logs]="timelineLogs"
            [loading]="timelineLoading"
            [error]="timelineError"
            [currentPage]="timelinePage"
            [totalPages]="timelineTotalPages"
            [totalElements]="timelineTotalElements"
            emptyMessage="No hay eventos de auditoría para esta empresa."
            (pageChange)="cargarTimeline($event)" />
        }
      </div>

      <!-- Footer -->
      <div class="shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <button type="button" (click)="onEdit()"
          class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
          Editar
        </button>
        <button type="button" (click)="onClose()"
          class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-brand-500 text-white hover:bg-brand-600">
          Cerrar
        </button>
      </div>
    </div>
  }
</app-modal>
